import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarIcon } from "lucide-react";
import { format, startOfDay } from "date-fns";
import { cn } from "@/lib/utils";
import { UseFormReturn } from "react-hook-form";
import { FormData } from "../types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface InvoiceHeaderProps {
  form: UseFormReturn<FormData>;
  issueDate: Date;
}

export const InvoiceHeader = ({ form, issueDate }: InvoiceHeaderProps) => {
  const invoiceNumber = form.watch("invoiceNumber");
  const { t } = useTranslation();

  useEffect(() => {
    // Update payment reference when invoice number changes
    if (invoiceNumber) {
      form.setValue("paymentReference", invoiceNumber);
    }
  }, [invoiceNumber, form]);

  return (
    <Card className="border-2 border-[#34375B]">
      <CardHeader className="p-3 md:p-6">
        <CardTitle className="text-[#34375B] text-lg md:text-xl">{t("invoice_header.title", "Invoice Header")}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 md:p-6 space-y-3 md:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="invoiceNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#8791F3] text-sm md:text-base">{t("invoice_header.invoice_number", "Invoice Number")}</FormLabel>
                <FormControl>
                  <Input placeholder="INV-001" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="paymentReference"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#8791F3] text-sm md:text-base">{t("invoice_header.payment_reference", "Payment Reference")}</FormLabel>
                <FormControl>
                  <Input placeholder="INV-001" className="placeholder:text-[#9397A8] bg-white border border-gray-300 p-2 rounded-md text-sm md:text-base" {...field} />
                </FormControl>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <FormField
            control={form.control}
            name="issueDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-[#8791F3] text-sm md:text-base">{t("invoice_header.issue_date", "Issue Date")}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full sm:w-[240px] pl-3 bg-white text-[#34375B] hover:bg-white hover:text-[#34375B] text-left font-normal text-sm md:text-base",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>{t("invoice_header.pick_a_date", "Pick a date")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      initialFocus
                      className="[&_.rdp-day_focus]:bg-[#8791f3] [&_.rdp-day_selected]:bg-[#8791f3]"
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="dueDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="text-[#8791F3] text-sm md:text-base">{t("invoice_header.due_date", "Due Date")}</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full sm:w-[240px] pl-3 bg-white text-[#34375B] hover:bg-white hover:text-[#34375B] text-left font-normal text-sm md:text-base",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>{t("invoice_header.pick_a_date", "Pick a date")}</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => {
                        const startOfSelectedDate = startOfDay(date);
                        const startOfIssueDate = startOfDay(issueDate);
                        return startOfSelectedDate <= startOfIssueDate;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage className="text-xs md:text-sm" />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};
